// UserInfoDialog — port of user-info-dialog + manual-id-reject-dialog
// Rendered as a full page at /manual-id-verification/user-info
// Reads record from router location.state.data (set by ManualIdVerificationPage row click)
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Swal from 'sweetalert2';
import {
  useVerifyManualIdMutation,
  useRejectManualIdMutation,
  useCountriesQuery,
} from './api';
import { EmailPreview } from '@/components/EmailPreview';
import { RichTextEditor } from '@/components/RichTextEditor';
import {
  IdDocumentTypeEnum,
  IdentityVerificationStatusEnum,
  ManualIdVerificationStatusEnum,
} from '@/types/manual-id';
import type {
  AirtableAttachment,
  Country,
  ManualIdVerificationRecord,
  RejectionReason,
} from '@/types/manual-id';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getStatusClass(status: string): string {
  switch (status.toUpperCase()) {
    case IdentityVerificationStatusEnum.NOT_VERIFIED:
    case IdentityVerificationStatusEnum.IN_PROGRESS:
      return 'mode_done';
    case IdentityVerificationStatusEnum.BLOCKED:
      return 'mode_process';
    case IdentityVerificationStatusEnum.REJECTED:
      return 'mode_off';
    case IdentityVerificationStatusEnum.APPROVED_VERIFIED:
      return 'mode_on';
    default:
      return '';
  }
}

function useTranslateStatus() {
  const { t } = useTranslation();
  return (status: string) => {
    switch (status.toUpperCase()) {
      case IdentityVerificationStatusEnum.APPROVED_VERIFIED:
        return t('global.identityVerificationStatus.verified', { defaultValue: 'Verified' });
      case IdentityVerificationStatusEnum.IN_PROGRESS:
        return t('global.identityVerificationStatus.in_progress', { defaultValue: 'In progress' });
      case IdentityVerificationStatusEnum.NOT_VERIFIED:
        return t('global.identityVerificationStatus.not_verified', { defaultValue: 'Not verified' });
      case IdentityVerificationStatusEnum.REJECTED:
        return t('global.identityVerificationStatus.rejected', { defaultValue: 'Rejected' });
      case IdentityVerificationStatusEnum.BLOCKED:
        return t('global.identityVerificationStatus.need_help', { defaultValue: 'Need help' });
      default:
        return status;
    }
  };
}

function translateDocumentType(type: string, t: (k: string, opts: { defaultValue: string }) => string): string {
  switch (type.toUpperCase()) {
    case IdDocumentTypeEnum.NATIONAL_IDENTITY_CARD:
      return t('global.idDocumentType.national_identity_card', { defaultValue: 'National identity card' });
    case IdDocumentTypeEnum.PASSPORT:
      return t('global.idDocumentType.passport', { defaultValue: 'Passport' });
    case IdDocumentTypeEnum.RECEIPT:
      return t('global.idDocumentType.receipt', { defaultValue: 'Receipt' });
    default:
      return type;
  }
}

interface DocumentPreview {
  id: string;
  url: string;
  type: string;
  index: number;
}

function buildPreview(attachment: AirtableAttachment, id: string, index: number): DocumentPreview {
  return { id, url: attachment.url, type: attachment.type, index };
}

// ─── Confirm assessment form schema ─────────────────────────────────────────

const assessSchema = z.object({
  idDoc: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  birthDate: z.string().min(1),
  expiryDate: z.string().min(1),
  placeOfBirth: z.string().min(1),
  issuingDate: z.string().min(1),
  nationalityCtrl: z.string().min(1),
  issuingCountryName: z.string().min(1),
});
type AssessFormValues = z.infer<typeof assessSchema>;

// ─── Reject Dialog ───────────────────────────────────────────────────────────

interface RejectDialogProps {
  record: ManualIdVerificationRecord;
  isWaitingForResponse: boolean;
  onClose: (success: boolean) => void;
}

function RejectDialog({ record, isWaitingForResponse, onClose }: RejectDialogProps) {
  const { t } = useTranslation();
  const rejectMutation = useRejectManualIdMutation();
  const [previewMode, setPreviewMode] = useState(false);
  const [reasonForRejection, setReasonForRejection] = useState(
    record.fields['Reason for rejection'] ?? '',
  );
  const [note, setNote] = useState(record.fields.Note ?? '');

  // Load rejection reasons from i18n
  const reasonsRaw = t('global.id-rejection-reasons', {
    returnObjects: true,
    defaultValue: [] as RejectionReason[],
  });
  const reasons = Array.isArray(reasonsRaw) ? (reasonsRaw as RejectionReason[]) : [];

  const getUserLanguage = () => {
    if (record.fields.Language === 'fr' || record.fields.language === 'fr') {
      return t('french', { defaultValue: 'French' });
    }
    return t('english', { defaultValue: 'English' });
  };

  const handleReject = async () => {
    const updated: ManualIdVerificationRecord = {
      ...record,
      fields: {
        ...record.fields,
        'Reason for rejection': reasonForRejection,
        Note: note,
        'Id Document Verification Status': ManualIdVerificationStatusEnum.REJECTED,
      },
    };
    const res = await rejectMutation.mutateAsync(updated);
    if (res.code === 0) {
      onClose(true);
    } else {
      await Swal.fire(t('messages.error', { defaultValue: 'Error' }), res.message, 'error');
    }
  };

  return (
    <Dialog open onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ cursor: 'move' }}>
        {t('pages.manualIdVerificationDetails.reject-dialog.title', {
          defaultValue: 'Rejection',
        })}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ pb: 2 }}>
          {t('pages.manualIdVerificationDetails.reject-dialog.question1', {
            defaultValue: 'Are you sure you want to reject this verification?',
          })}
        </Typography>

        <Stack direction="row" sx={{ mb: 2 }} alignItems="center" spacing={1}>
          <Typography variant="subtitle1">
            {t('pages.manualIdVerificationDetails.reject-dialog.user-language', {
              defaultValue: 'User language:',
            })}
          </Typography>
          <Chip label={getUserLanguage()} color="success" size="small" />
        </Stack>

        {!previewMode && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('pages.manualIdVerificationDetails.reject-dialog.reason', {
                defaultValue: 'Reason',
              })}
            </Typography>
            <Select
              fullWidth
              size="small"
              value={reasonForRejection}
              disabled={!isWaitingForResponse}
              onChange={(e) => setReasonForRejection(e.target.value)}
              displayEmpty
              sx={{ mb: 2 }}
            >
              <MenuItem value="" disabled>
                {t('pages.manualIdVerificationDetails.reject-dialog.choose-item', {
                  defaultValue: '— Choose a reason —',
                })}
              </MenuItem>
              {reasons.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.description}
                </MenuItem>
              ))}
            </Select>

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t('pages.manualIdVerificationDetails.reject-dialog.note', { defaultValue: 'Note' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('pages.manualIdVerificationDetails.reject-dialog.note-description', {
                defaultValue: 'This note will be included in the rejection email.',
              })}
            </Typography>
            <RichTextEditor
              value={note}
              onChange={setNote}
              editable={isWaitingForResponse}
              minHeight={213}
            />
          </>
        )}

        {previewMode && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              *{t('pages.manualIdVerificationDetails.reject-dialog.email_preview_notice', {
                defaultValue: 'Preview of the email that will be sent to the user.',
              })}
            </Typography>
            <EmailPreview html={note} height={400} />
          </>
        )}
      </DialogContent>

      <DialogActions>
        {previewMode && (
          <Button onClick={() => setPreviewMode(false)}>
            {t('pages.manualIdVerificationDetails.reject-dialog.go_back', {
              defaultValue: 'Go back',
            })}
          </Button>
        )}
        {!previewMode && (
          <Button onClick={() => onClose(false)}>
            {t('pages.manualIdVerificationDetails.reject-dialog.cancel', {
              defaultValue: 'Cancel',
            })}
          </Button>
        )}
        {isWaitingForResponse && previewMode && (
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleReject()}
            disabled={rejectMutation.isPending}
          >
            {t('pages.manualIdVerificationDetails.reject-dialog.reject', {
              defaultValue: 'Reject',
            })}
          </Button>
        )}
        {isWaitingForResponse && !previewMode && (
          <Button variant="outlined" color="warning" onClick={() => setPreviewMode(true)}>
            {t('pages.manualIdVerificationDetails.reject-dialog.preview_email', {
              defaultValue: 'Preview email',
            })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function UserInfoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const translateStatus = useTranslateStatus();

  const record: ManualIdVerificationRecord | undefined = (
    location.state as { data?: ManualIdVerificationRecord } | null
  )?.data;

  // Redirect if no record in state
  if (!record) {
    navigate('/manual-id-verification', { replace: true });
    return null;
  }

  return <UserInfoPageContent record={record} translateStatus={translateStatus} />;
}

// Split into inner component so hooks run unconditionally after the guard above.
interface ContentProps {
  record: ManualIdVerificationRecord;
  translateStatus: (s: string) => string;
}

function UserInfoPageContent({ record: initialRecord, translateStatus }: ContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const verifyMutation = useVerifyManualIdMutation();
  const countriesQuery = useCountriesQuery();
  const countries: Country[] = countriesQuery.data ?? [];

  const [displayDocuments, setDisplayDocuments] = useState(false);
  const [currentAction, setCurrentAction] = useState<'' | 'confirm'>('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomedIn, setZoomedIn] = useState(false);

  const idDocs = initialRecord.fields['Id Doc'];
  const selfie = initialRecord.fields['Selfie Photo'];

  const buildSlides = (): DocumentPreview[] => {
    const slides: DocumentPreview[] = [buildPreview(idDocs[0], 'front', 0)];
    if (idDocs.length >= 2) slides.push(buildPreview(idDocs[1], 'back', 1));
    if (selfie?.[0]) slides.push(buildPreview(selfie[0], 'face', slides.length));
    return slides;
  };
  const slides = buildSlides();

  const [previewIdx, setPreviewIdx] = useState(0);
  const preview = slides[previewIdx];

  const status = initialRecord.fields['Id Document Verification Status'];
  const isWaitingForResponse =
    status.toUpperCase() === IdentityVerificationStatusEnum.BLOCKED ||
    status.toUpperCase() === IdentityVerificationStatusEnum.IN_PROGRESS ||
    status.toUpperCase() === IdentityVerificationStatusEnum.NOT_VERIFIED;
  const isRejected =
    status.toUpperCase() === IdentityVerificationStatusEnum.REJECTED || isWaitingForResponse;
  const isRejectedStatus = status.toUpperCase() === IdentityVerificationStatusEnum.REJECTED;
  const isWaitingStatus = status.toUpperCase() === IdentityVerificationStatusEnum.IN_PROGRESS;

  const currentDocumentType = translateDocumentType(
    initialRecord.fields['Id Document Type'],
    t,
  );

  // ─── Assessment form ───────────────────────────────────────────────────────

  const defaultNationality =
    countries.find(
      (c) =>
        initialRecord.fields.Nationality &&
        c.iso3.toUpperCase() === initialRecord.fields.Nationality.toUpperCase(),
    ) ?? null;

  const defaultIssuingCountry =
    countries.find(
      (c) =>
        initialRecord.fields['Issuing Country'] &&
        c.iso3.toUpperCase() === initialRecord.fields['Issuing Country'].toUpperCase(),
    ) ?? null;

  const { control, handleSubmit, formState } = useForm<AssessFormValues>({
    resolver: zodResolver(assessSchema),
    defaultValues: {
      idDoc: initialRecord.fields['Id Document Number'] ?? '',
      firstName: initialRecord.fields['First Name'],
      lastName: initialRecord.fields['Last Name'],
      birthDate: initialRecord.fields['Birth Date']
        ? String(initialRecord.fields['Birth Date'])
        : '',
      expiryDate: initialRecord.fields['Id Document Expiry Date']
        ? String(initialRecord.fields['Id Document Expiry Date'])
        : '',
      placeOfBirth: initialRecord.fields['Place of birth'] ?? '',
      issuingDate: initialRecord.fields['Issuing Date']
        ? String(initialRecord.fields['Issuing Date'])
        : '',
      nationalityCtrl: defaultNationality?.name ?? '',
      issuingCountryName: defaultIssuingCountry?.name ?? '',
    },
  });

  const [selectedNationality, setSelectedNationality] = useState<Country | null>(defaultNationality);
  const [selectedIssuingCountry, setSelectedIssuingCountry] = useState<Country | null>(defaultIssuingCountry);

  const onConfirm = handleSubmit(async (values) => {
    if (!selectedNationality || !selectedIssuingCountry) {
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        t('messages.error-fields', { defaultValue: 'Please fill in all required fields.' }),
        'error',
      );
      return;
    }

    const expiryDate = new Date(values.expiryDate);
    const thirtyDaysOut = new Date();
    thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);

    const doVerify = async () => {
      const updatedRecord: ManualIdVerificationRecord = {
        ...initialRecord,
        fields: {
          ...initialRecord.fields,
          'Id Document Number': values.idDoc,
          'First Name': values.firstName,
          'Last Name': values.lastName,
          'Birth Date': values.birthDate,
          'Id Document Expiry Date': values.expiryDate,
          'Place of birth': values.placeOfBirth,
          Nationality: selectedNationality.iso3,
          'Issuing Date': values.issuingDate,
          'Issuing Country': selectedIssuingCountry.iso3,
          'Id Document Verification Status': IdentityVerificationStatusEnum.APPROVED_VERIFIED,
        },
      };
      const res = await verifyMutation.mutateAsync(updatedRecord);
      if (res.code === 0) {
        await Swal.fire({
          position: 'top-end',
          icon: 'success',
          text: t('messages.id-doc-processed-successfully', {
            defaultValue: 'ID document processed successfully.',
          }),
          showConfirmButton: false,
          timer: 1500,
        });
        navigate('/manual-id-verification');
      } else {
        await Swal.fire(t('messages.error', { defaultValue: 'Error' }), res.message, 'error');
      }
    };

    if (expiryDate <= thirtyDaysOut) {
      const result = await Swal.fire({
        title: t('messages.are-you-sure', { defaultValue: 'Are you sure?' }),
        text: t('messages.id-document-expiry-date-soon', {
          defaultValue: 'The document expiry date is within 30 days.',
        }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: t('messages.yes-continue', { defaultValue: 'Yes, continue' }),
        cancelButtonText: t('messages.no-cancel', { defaultValue: 'No, cancel' }),
      });
      if (result.isConfirmed) await doVerify();
    } else {
      await doVerify();
    }
  });

  // ─── Navigation ────────────────────────────────────────────────────────────

  const totalSlides = slides.length;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box className="container-fluid page" sx={{ p: 2 }}>
      <Box className="d-flex page__box p-3 mt-2">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/manual-id-verification')}
          sx={{ textTransform: 'none' }}
        >
          {t('menus.menuManualIdVerificationDetails', {
            defaultValue: 'Manual ID Verification — User Info',
          })}
        </Button>
      </Box>

      <Box className="page__content shadow p-3 position-relative" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Left panel: user info */}
          <Grid item xs={12} md={5} sx={{ minWidth: 340 }}>
            <Card>
              <CardHeader
                title={
                  <Typography variant="h6">
                    {t('pages.manualIdVerificationDetails.userProfile', {
                      defaultValue: 'User Profile',
                    })}
                  </Typography>
                }
              />
              <Divider />
              <CardContent>
                <InfoRow
                  label={t('pages.manualIdVerificationDetails.firstName', { defaultValue: 'First name' })}
                  value={initialRecord.fields['First Name']}
                />
                <InfoRow
                  label={t('pages.manualIdVerificationDetails.lastName', { defaultValue: 'Last name' })}
                  value={initialRecord.fields['Last Name']}
                />
                <InfoRow
                  label={t('pages.manualIdVerificationDetails.email', { defaultValue: 'Email' })}
                  value={initialRecord.fields.Email}
                />
                <InfoRow
                  label={t('pages.manualIdVerificationDetails.gender', { defaultValue: 'Gender' })}
                  value={initialRecord.fields.Gender}
                />
                <InfoRow
                  label={t('pages.manualIdVerificationDetails.birthDate', { defaultValue: 'Birth date' })}
                  value={initialRecord.fields['Birth Date'] ? String(initialRecord.fields['Birth Date']) : '—'}
                />
              </CardContent>

              <CardHeader
                title={
                  <Typography variant="h6">
                    {t('pages.manualIdVerificationDetails.businessDetails', {
                      defaultValue: 'Business Details',
                    })}
                  </Typography>
                }
              />
              <Divider />
              <CardContent>
                <InfoRow
                  label={t('pages.manualIdVerificationDetails.businessName', { defaultValue: 'Business name' })}
                  value={initialRecord.fields['Company Name'] ?? '—'}
                />
                <InfoRow
                  label={t('pages.manualIdVerificationDetails.profile', { defaultValue: 'Profile' })}
                  value={initialRecord.fields.Role}
                />
              </CardContent>

              <CardHeader
                title={
                  <Typography variant="h6">
                    {t('pages.manualIdVerificationDetails.idVerification', {
                      defaultValue: 'ID Verification',
                    })}
                  </Typography>
                }
              />
              <Divider />
              <CardContent>
                <InfoRow
                  label={t('pages.manualIdVerificationDetails.documentType', {
                    defaultValue: 'Document type',
                  })}
                  value={currentDocumentType}
                />
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('pages.manualIdVerificationDetails.status', { defaultValue: 'Status' })}
                  </Typography>
                  {status && (
                    <span className={`mode ${getStatusClass(status)}`}>
                      {translateStatus(status)}
                    </span>
                  )}
                </Box>
                {initialRecord.fields['Onfido Check Result']?.[0] && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {t('pages.manualIdVerificationDetails.onfidoReport', {
                        defaultValue: 'Onfido report',
                      })}
                    </Typography>
                    <Link
                      href={initialRecord.fields['Onfido Check Result'][0].url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <PictureAsPdfIcon
                        fontSize="large"
                        color="error"
                        titleAccess={t(
                          'pages.manualIdVerificationDetails.downloadOnfidoReport',
                          { defaultValue: 'Download Onfido report' },
                        )}
                      />
                    </Link>
                  </Box>
                )}
                {!displayDocuments && (
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardIosIcon fontSize="small" />}
                    onClick={() => setDisplayDocuments(true)}
                    sx={{ mt: 1 }}
                  >
                    {t('pages.manualIdVerificationDetails.review', { defaultValue: 'Review' })}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right panel: document viewer or assessment form */}
          {displayDocuments && (
            <Grid item xs={12} md={7} sx={{ minWidth: 340 }}>
              {currentAction === '' && (
                <Card sx={{ pb: 3 }}>
                  <CardHeader
                    title={t('pages.manualIdVerificationDetails.idCardReview', {
                      defaultValue: 'ID Card Review',
                    })}
                    subheader={
                      <Stack spacing={0.5} sx={{ pt: 1 }}>
                        <Typography variant="body2">
                          {t('pages.manualIdVerificationDetails.question1', {
                            defaultValue: 'Is the document valid?',
                          })}
                        </Typography>
                        <Typography variant="body2">
                          {t('pages.manualIdVerificationDetails.question2', {
                            defaultValue: 'Does the photo match?',
                          })}
                        </Typography>
                        <Typography variant="body2">
                          {t('pages.manualIdVerificationDetails.question3', {
                            defaultValue: 'Is the information correct?',
                          })}
                        </Typography>
                      </Stack>
                    }
                  />
                  <DocumentViewer
                    preview={preview}
                    previewIdx={previewIdx}
                    totalSlides={totalSlides}
                    onPrev={() => setPreviewIdx((i) => Math.max(0, i - 1))}
                    onNext={() => setPreviewIdx((i) => Math.min(totalSlides - 1, i + 1))}
                    onEnlarge={() => { setZoomedIn(false); setZoomOpen(true); }}
                  />
                  {(isWaitingForResponse || isRejected) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                      {isWaitingForResponse && (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => {
                            setCurrentAction('confirm');
                            setPreviewIdx(0);
                          }}
                        >
                          {t('pages.manualIdVerificationDetails.confirm', {
                            defaultValue: 'Confirm',
                          })}
                        </Button>
                      )}
                      {isRejectedStatus && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => setRejectDialogOpen(true)}
                        >
                          {t('pages.manualIdVerificationDetails.details', {
                            defaultValue: 'Details',
                          })}
                        </Button>
                      )}
                      {(isWaitingStatus || isWaitingForResponse) && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => setRejectDialogOpen(true)}
                        >
                          {t('pages.manualIdVerificationDetails.reject', {
                            defaultValue: 'Reject',
                          })}
                        </Button>
                      )}
                    </Box>
                  )}
                </Card>
              )}

              {currentAction === 'confirm' && (
                <Card sx={{ pb: 3 }}>
                  <CardHeader
                    title={
                      <Stack spacing={1}>
                        <Button
                          startIcon={<ArrowBackIosIcon fontSize="small" />}
                          onClick={() => setCurrentAction('')}
                          sx={{ textTransform: 'none', justifyContent: 'flex-start', width: 'fit-content' }}
                        >
                          {t('pages.manualIdVerificationDetails.assessment.back', {
                            defaultValue: 'Back',
                          })}
                        </Button>
                        <Typography variant="h6">
                          {t('pages.manualIdVerificationDetails.assessment.title', {
                            defaultValue: 'Assessment',
                          })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('pages.manualIdVerificationDetails.assessment.review-approve-document', {
                            defaultValue: 'Review and approve the document details below.',
                          })}
                        </Typography>
                      </Stack>
                    }
                  />
                  <DocumentViewer
                    preview={preview}
                    previewIdx={previewIdx}
                    totalSlides={totalSlides}
                    onPrev={() => setPreviewIdx((i) => Math.max(0, i - 1))}
                    onNext={() => setPreviewIdx((i) => Math.min(totalSlides - 1, i + 1))}
                    onEnlarge={() => { setZoomedIn(false); setZoomOpen(true); }}
                  />
                  <CardContent>
                    {/* Assessment form */}
                    <form onSubmit={(e) => void onConfirm(e)}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label={t('pages.manualIdVerificationDetails.assessment.documentType', {
                              defaultValue: 'Document type',
                            })}
                            value={currentDocumentType}
                            fullWidth
                            size="small"
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="idDoc"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('pages.manualIdVerificationDetails.assessment.idDoc', {
                                  defaultValue: 'ID document number',
                                })}
                                fullWidth
                                size="small"
                                error={!!formState.errors.idDoc}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="firstName"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('pages.manualIdVerificationDetails.assessment.firstName', {
                                  defaultValue: 'First name',
                                })}
                                fullWidth
                                size="small"
                                error={!!formState.errors.firstName}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="lastName"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('pages.manualIdVerificationDetails.assessment.lastName', {
                                  defaultValue: 'Last name',
                                })}
                                fullWidth
                                size="small"
                                error={!!formState.errors.lastName}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="birthDate"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="date"
                                label={t('pages.manualIdVerificationDetails.assessment.birthDate', {
                                  defaultValue: 'Birth date',
                                })}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={!!formState.errors.birthDate}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="expiryDate"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="date"
                                label={t('pages.manualIdVerificationDetails.assessment.expiryDate', {
                                  defaultValue: 'Expiry date',
                                })}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={!!formState.errors.expiryDate}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="placeOfBirth"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('pages.manualIdVerificationDetails.assessment.placeOfBirth', {
                                  defaultValue: 'Place of birth',
                                })}
                                fullWidth
                                size="small"
                                error={!!formState.errors.placeOfBirth}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="nationalityCtrl"
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                options={countries}
                                getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                                value={selectedNationality}
                                onChange={(_, val) => {
                                  setSelectedNationality(val);
                                  field.onChange(val?.name ?? '');
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={t(
                                      'pages.manualIdVerificationDetails.assessment.nationality',
                                      { defaultValue: 'Nationality' },
                                    )}
                                    size="small"
                                    error={!!formState.errors.nationalityCtrl}
                                  />
                                )}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="issuingDate"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="date"
                                label={t('pages.manualIdVerificationDetails.assessment.issuingDate', {
                                  defaultValue: 'Issuing date',
                                })}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={!!formState.errors.issuingDate}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="issuingCountryName"
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                options={countries}
                                getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                                value={selectedIssuingCountry}
                                onChange={(_, val) => {
                                  setSelectedIssuingCountry(val);
                                  field.onChange(val?.name ?? '');
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={t(
                                      'pages.manualIdVerificationDetails.assessment.issuingCountry',
                                      { defaultValue: 'Issuing country' },
                                    )}
                                    size="small"
                                    error={!!formState.errors.issuingCountryName}
                                  />
                                )}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setRejectDialogOpen(true)}
                            fullWidth
                          >
                            {t('pages.manualIdVerificationDetails.assessment.reject', {
                              defaultValue: 'Reject',
                            })}
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="success"
                            fullWidth
                            disabled={verifyMutation.isPending}
                          >
                            {t('pages.manualIdVerificationDetails.assessment.save', {
                              defaultValue: 'Save & Approve',
                            })}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </CardContent>
                </Card>
              )}
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Reject dialog */}
      {rejectDialogOpen && (
        <RejectDialog
          record={initialRecord}
          isWaitingForResponse={isWaitingForResponse}
          onClose={(success) => {
            setRejectDialogOpen(false);
            if (success) {
              void Swal.fire({
                position: 'top-end',
                icon: 'success',
                text: t('messages.id-doc-processed-successfully', {
                  defaultValue: 'ID document processed successfully.',
                }),
                showConfirmButton: false,
                timer: 1500,
              }).then(() => navigate('/manual-id-verification'));
            }
          }}
        />
      )}

      {/* Zoom modal */}
      {zoomOpen && preview && (
        <Box
          id="myModal"
          className="modal"
          onClick={() => setZoomOpen(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <IconButton
              onClick={() => setZoomOpen(false)}
              sx={{ position: 'absolute', top: 8, right: 8, color: '#fff' }}
            >
              ✕
            </IconButton>
            <img
              id="img01"
              src={preview.url}
              alt="preview"
              onClick={(e) => {
                e.stopPropagation();
                setZoomedIn((z) => !z);
              }}
              style={{
                maxHeight: '80vh',
                maxWidth: '100%',
                cursor: 'zoom-in',
                transform: zoomedIn ? 'scale(1.5)' : 'scale(1)',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', mb: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

interface DocumentViewerProps {
  preview: DocumentPreview;
  previewIdx: number;
  totalSlides: number;
  onPrev: () => void;
  onNext: () => void;
  onEnlarge: () => void;
}

function DocumentViewer({
  preview,
  previewIdx,
  totalSlides,
  onPrev,
  onNext,
  onEnlarge,
}: DocumentViewerProps) {
  const isImage = preview.type.startsWith('image');
  return (
    <Box sx={{ textAlign: 'center', px: 2, py: 2, position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          mb: 1,
        }}
      >
        <IconButton onClick={onPrev} disabled={previewIdx === 0} size="small">
          <ArrowBackIosIcon />
        </IconButton>
        <IconButton onClick={onNext} disabled={previewIdx >= totalSlides - 1} size="small">
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>

      {!isImage && (
        <Box
          component="embed"
          src={preview.url}
          type={preview.type}
          sx={{ width: '100%', height: 500 }}
        />
      )}
      {isImage && (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <IconButton
            onClick={onEnlarge}
            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.4)', color: '#fff' }}
            size="small"
          >
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
          <Box
            component="img"
            src={preview.url}
            alt={preview.id}
            onClick={onEnlarge}
            sx={{ maxWidth: '100%', maxHeight: 500, cursor: 'pointer' }}
          />
        </Box>
      )}
    </Box>
  );
}
